import useTheme from '../../hooks/ui/useTheme';
import CustomButton from './CustomButton';
import useOrderingDisabledStatus from '../../hooks/availability/useOrderingDisabledStatus';
import { useTranslation } from 'react-i18next';
import useBasket from '../../hooks/basket/useBasket';
import { SCREENS } from '../../services/exports/Constants';
import useScreenType from '../../hooks/utility/useScreenType';
import useRouter from '../../hooks/navigation/useRouter';
import classnames from 'classnames';
import { ButtonTypes } from '../../services/exports/Interfaces';
import LoadingIndicator from '../loading/LoadingIndicator';
import OrderBasketCashback from '../cashback/OrderBasketCashback';
import PriceComparison from '../menu/PriceComparison';
import useMobileApp from '../../hooks/mobile-app/useMobileApp';
import { ShieldCheck } from 'lucide-react';

interface Props {
  title?: string;
  loading?: boolean;
  disabled?: boolean;
  hideAddProductsButton?: boolean;
  hideLoginPrompt?: boolean;
  hideCashback?: boolean;
  className?: string;
  children?: React.ReactNode;
  onClick?: () => void;
}

export default function CheckoutButton(props: Props) {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Buttons:CheckoutButton' });

  const {
    title,
    loading,
    disabled,
    hideAddProductsButton,
    hideLoginPrompt,
    onClick,
    className,
    children,
    hideCashback,
  } = props;

  const { isMobile } = useScreenType();
  const { navigate } = useRouter();
  const { render: renderOrderingDisabled } = useOrderingDisabledStatus({ className });
  const { insets } = useMobileApp();

  const { quantity, subtotal, total } = useBasket();

  function _onClick() {
    if (loading || disabled) {
      return;
    }

    return onClick();
  }

  const renderEmptyBasketButton = (): JSX.Element => {
    if (quantity !== 0) {
      return null;
    }

    if (isMobile && !hideAddProductsButton) {
      return (
        <CustomButton
          title={t('add_products')}
          buttonType={ButtonTypes.third}
          onClick={() =>
            navigate({
              web: {
                to: SCREENS.HOME_SCREEN,
              },
            })
          }
        />
      );
    }
  };

  const renderMainButton = () =>
    quantity > 0 && (
      <>
        {!hideCashback && (
          <OrderBasketCashback subtotal={subtotal} total={total} className="mb-sm" hideLoginPrompt={hideLoginPrompt} />
        )}
        {children ? (
          children
        ) : (
          <CheckoutButton.Button disabled={disabled} loading={loading} title={title} onClick={_onClick} />
        )}
      </>
    );

  if (renderOrderingDisabled) {
    return renderOrderingDisabled;
  }

  const content = renderEmptyBasketButton() ?? renderMainButton();

  return (
    <div
      className={classnames(container(!!content), className, {
        'safe-area-bottom': insets.bottom > 0,
      })}
    >
      {content}
    </div>
  );
}

const container = (withContent: boolean) =>
  classnames('z-50', {
    'bg-brand-card border-t p-sm browser-safe-area-bottom': withContent,
  });

const buttonStyle =
  'flex flex-row w-full rounded-lg p-sm font-medium text-sm shadow-none group transition-all duration-200 ease-in-out';

CheckoutButton.Button = ({ disabled, loading, title, onClick, className }: Props) => {
  const { subtotal, total } = useBasket();
  const theme = useTheme();
  const { isMobileApp } = useMobileApp();

  const textStyle = {
    fontSize: isMobileApp ? 17 : 15,
    fontWeight: isMobileApp ? theme.font.weight.normal : theme.font.weight.medium,
  };

  return (
    <CustomButton
      disabled={disabled}
      onClick={onClick}
      className={classnames(buttonStyle, className, {
        'justify-between': !loading,
        'justify-center': loading,
        'cursor-not-allowed': disabled,
      })}
      buttonType={ButtonTypes.primary}
      showArrow={true}
    >
      {loading && <LoadingIndicator customColor={theme.buttons[ButtonTypes.primary].text} lg={20} md={20} sm={20} />}
      {!loading && (
        <>
          <div className="w-full flex flex-row items-center justify-center gap-x-[6px]">
            <ShieldCheck
              className={classnames('h-4 w-4 mr-sm flex-shrink-0 text-brand-button-primary-text', {
                '!text-brand-button-disabled-text': disabled,
              })}
            />
            <span
              className={classnames('text-brand-button-primary-text', {
                '!text-brand-button-disabled-text': disabled,
              })}
              style={textStyle}
            >
              {title}
            </span>
            <span
              className={classnames('text-brand-button-primary-text', {
                '!text-brand-button-disabled-text': disabled,
              })}
              style={textStyle}
            >
              {'·'}
            </span>
            <PriceComparison
              actualPrice={total}
              originalPrice={subtotal}
              actualPriceStyle={{
                ...textStyle,
                color: disabled ? theme.buttons[ButtonTypes.disabled].text : theme.buttons[ButtonTypes.primary].text,
              }}
              originalPriceStyle={{
                ...textStyle,
                color: disabled ? theme.buttons[ButtonTypes.disabled].text : theme.buttons[ButtonTypes.primary].text,
              }}
            />
          </div>
          <CustomButton.Arrow loading={loading} disabled={disabled} color={theme.buttons[ButtonTypes.primary].text} />
        </>
      )}
    </CustomButton>
  );
};
